import React, { useState, useCallback, useRef } from "react";
import {
  Eye,
  EyeOff,
  Plus,
  Trash2,
  ChevronUp,
  ChevronDown,
  Copy,
  Settings,
  Layers,
  Download,
  Upload,
} from "lucide-react";
import { LayerStackManager, LayerUtils } from "../lib/substrate/layerStack";
import { SubstrateEngine } from "../lib/substrate/substrateEngine";
import type {
  PrintLayer,
  LayerStack,
  SubstrateProfile,
  MultiLayerSimulationResult,
  LayerBlendMode,
} from "../types/substrate";

interface LayerStackPanelProps {
  layerStack: LayerStack;
  substrate: SubstrateProfile;
  onLayerStackChange?: (layerStack: LayerStack) => void;
  onSimulationResult?: (result: MultiLayerSimulationResult) => void;
  className?: string;
}

interface LayerEditorProps {
  layer: PrintLayer;
  onUpdate: (updates: Partial<PrintLayer>) => void;
  onDelete: () => void;
  onDuplicate: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  canMoveUp: boolean;
  canMoveDown: boolean;
}

const BLEND_MODES: {
  value: LayerBlendMode;
  label: string;
  description: string;
}[] = [
  {
    value: "multiply",
    label: "Multiply",
    description: "Standard subtractive blending",
  },
  { value: "screen", label: "Screen", description: "Lightens colors" },
  {
    value: "overlay",
    label: "Overlay",
    description: "Combines multiply and screen",
  },
  {
    value: "hard-light",
    label: "Hard Light",
    description: "Stronger overlay effect",
  },
  {
    value: "soft-light",
    label: "Soft Light",
    description: "Gentler overlay effect",
  },
  { value: "normal", label: "Normal", description: "Direct ink application" },
  {
    value: "overprint",
    label: "Overprint",
    description: "Ink overprints previous layers",
  },
  { value: "knockout", label: "Knockout", description: "Removes ink below" },
];

function LayerEditor({
  layer,
  onUpdate,
  onDelete,
  onDuplicate,
  onMoveUp,
  onMoveDown,
  canMoveUp,
  canMoveDown,
}: LayerEditorProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);

  const handleColorChange = useCallback(
    (channel: keyof typeof layer.cmyk, value: number) => {
      onUpdate({
        cmyk: {
          ...layer.cmyk,
          [channel]: Math.max(0, Math.min(100, value)),
        },
      });
    },
    [layer, onUpdate],
  );

  const handleOpacityChange = useCallback(
    (opacity: number) => {
      onUpdate({ opacity: Math.max(0, Math.min(1, opacity)) });
    },
    [onUpdate],
  );

  const handleBlendModeChange = useCallback(
    (blendMode: LayerBlendMode) => {
      onUpdate({ blendMode });
    },
    [onUpdate],
  );

  const handleVisibilityToggle = useCallback(() => {
    onUpdate({ visible: !layer.visible });
  }, [layer.visible, onUpdate]);

  const layerHex = `#${Math.round(layer.cmyk.c * 2.55)
    .toString(16)
    .padStart(2, "0")}${Math.round(layer.cmyk.m * 2.55)
    .toString(16)
    .padStart(2, "0")}${Math.round(layer.cmyk.y * 2.55)
    .toString(16)
    .padStart(2, "0")}`;

  return (
    <div
      className={`border rounded-lg transition-all ${
        layer.visible
          ? "bg-white border-gray-200"
          : "bg-gray-50 border-gray-300 opacity-60"
      }`}
    >
      {/* Layer Header */}
      <div className="flex items-center p-3 space-x-3">
        {/* Drag Handle */}
        <div className="cursor-move text-gray-400">
          <Layers className="w-4 h-4" />
        </div>

        {/* Visibility Toggle */}
        <button
          onClick={handleVisibilityToggle}
          className="text-gray-500 hover:text-gray-700 transition-colors"
          title={layer.visible ? "Hide layer" : "Show layer"}
        >
          {layer.visible ? (
            <Eye className="w-4 h-4" />
          ) : (
            <EyeOff className="w-4 h-4" />
          )}
        </button>

        {/* Color Preview */}
        <div
          className="w-8 h-8 rounded border-2 border-gray-300 cursor-pointer"
          style={{ backgroundColor: layerHex }}
          onClick={() => setShowColorPicker(!showColorPicker)}
          title="Click to edit color"
        />

        {/* Layer Name */}
        <input
          type="text"
          value={layer.name}
          onChange={(e) => onUpdate({ name: e.target.value })}
          className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="Layer name"
        />

        {/* Layer Controls */}
        <div className="flex items-center space-x-1">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-1 text-gray-500 hover:text-gray-700 transition-colors"
            title="Layer settings"
          >
            <Settings
              className={`w-4 h-4 transition-transform ${isExpanded ? "rotate-90" : ""}`}
            />
          </button>

          <button
            onClick={onDuplicate}
            className="p-1 text-gray-500 hover:text-gray-700 transition-colors"
            title="Duplicate layer"
          >
            <Copy className="w-4 h-4" />
          </button>

          <button
            onClick={onMoveUp}
            disabled={!canMoveUp}
            className="p-1 text-gray-500 hover:text-gray-700 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            title="Move layer up"
          >
            <ChevronUp className="w-4 h-4" />
          </button>

          <button
            onClick={onMoveDown}
            disabled={!canMoveDown}
            className="p-1 text-gray-500 hover:text-gray-700 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            title="Move layer down"
          >
            <ChevronDown className="w-4 h-4" />
          </button>

          <button
            onClick={onDelete}
            className="p-1 text-red-500 hover:text-red-700 transition-colors"
            title="Delete layer"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Expanded Layer Settings */}
      {isExpanded && (
        <div className="px-3 pb-3 border-t border-gray-200 pt-3 space-y-3">
          {/* CMYK Color Controls */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              CMYK Values
            </label>
            <div className="grid grid-cols-4 gap-2">
              {(["c", "m", "y", "k"] as const).map((channel) => (
                <div key={channel} className="space-y-1">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-medium text-gray-600 uppercase">
                      {channel}
                    </label>
                    <span className="text-xs text-gray-500">
                      {layer.cmyk[channel].toFixed(0)}%
                    </span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={layer.cmyk[channel]}
                    onChange={(e) =>
                      handleColorChange(channel, parseFloat(e.target.value))
                    }
                    className="w-full h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                    style={{
                      background: `linear-gradient(to right, #e5e7eb 0%, #e5e7eb ${layer.cmyk[channel]}%, #3b82f6 ${layer.cmyk[channel]}%, #3b82f6 100%)`,
                    }}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Opacity Control */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700">
                Opacity
              </label>
              <span className="text-sm text-gray-500">
                {(layer.opacity * 100).toFixed(0)}%
              </span>
            </div>
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={layer.opacity}
              onChange={(e) => handleOpacityChange(parseFloat(e.target.value))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
          </div>

          {/* Blend Mode Control */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Blend Mode
            </label>
            <select
              value={layer.blendMode}
              onChange={(e) =>
                handleBlendModeChange(e.target.value as LayerBlendMode)
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            >
              {BLEND_MODES.map((mode) => (
                <option key={mode.value} value={mode.value}>
                  {mode.label} - {mode.description}
                </option>
              ))}
            </select>
          </div>

          {/* Layer Info */}
          <div className="text-xs text-gray-500 space-y-1">
            <div>TAC: {LayerUtils.calculateTAC(layer.cmyk).toFixed(0)}%</div>
            <div>zIndex: {layer.zIndex}</div>
            {layer.metadata && (
              <div>Type: {layer.metadata.layerType || "process"}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export function LayerStackPanel({
  layerStack,
  substrate,
  onLayerStackChange,
  onSimulationResult,
  className = "",
}: LayerStackPanelProps) {
  const [manager, setManager] = useState<LayerStackManager | null>(null);
  const [isSimulating, setIsSimulating] = useState(false);
  const [simulationResult, setSimulationResult] =
    useState<MultiLayerSimulationResult | null>(null);
  const [showExportOptions, setShowExportOptions] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Sync manager with incoming layerStack prop
  React.useEffect(() => {
    const newManager =
      LayerStackManager.import(JSON.stringify(layerStack)) ||
      new LayerStackManager(layerStack.id, layerStack.name, substrate);

    // Import existing layers if we have them
    if (layerStack.layers.length > 0) {
      newManager.clearLayers();
      layerStack.layers.forEach((layer) => {
        newManager.addLayer({
          name: layer.name,
          cmyk: layer.cmyk,
          opacity: layer.opacity,
          blendMode: layer.blendMode,
          visible: layer.visible,
          zIndex: layer.zIndex,
          metadata: layer.metadata,
        });
      });
    }

    newManager.setSubstrate(substrate);
    setManager(newManager);
  }, [layerStack, substrate]);

  const handleAddLayer = useCallback(() => {
    if (!manager) return;

    manager.addLayer({
      name: `Layer ${manager.getLayerCount() + 1}`,
      cmyk: { c: 0, m: 0, y: 0, k: 0 },
      opacity: 1,
      blendMode: "multiply",
      visible: true,
      zIndex: manager.getLayerCount(),
    });

    const updatedStack = manager.getLayerStack();
    onLayerStackChange?.(updatedStack);
  }, [manager, onLayerStackChange]);

  const handleLayerUpdate = useCallback(
    (layerId: string, updates: Partial<PrintLayer>) => {
      if (!manager) return;

      manager.updateLayer(layerId, updates);
      const updatedStack = manager.getLayerStack();
      onLayerStackChange?.(updatedStack);
    },
    [manager, onLayerStackChange],
  );

  const handleLayerDelete = useCallback(
    (layerId: string) => {
      if (!manager) return;

      manager.removeLayer(layerId);
      const updatedStack = manager.getLayerStack();
      onLayerStackChange?.(updatedStack);
    },
    [manager, onLayerStackChange],
  );

  const handleLayerDuplicate = useCallback(
    (layerId: string) => {
      if (!manager) return;

      manager.duplicateLayer(layerId);
      const updatedStack = manager.getLayerStack();
      onLayerStackChange?.(updatedStack);
    },
    [manager, onLayerStackChange],
  );

  const handleLayerMoveUp = useCallback(
    (layerId: string) => {
      if (!manager) return;

      manager.moveLayerUp(layerId);
      const updatedStack = manager.getLayerStack();
      onLayerStackChange?.(updatedStack);
    },
    [manager, onLayerStackChange],
  );

  const handleLayerMoveDown = useCallback(
    (layerId: string) => {
      if (!manager) return;

      manager.moveLayerDown(layerId);
      const updatedStack = manager.getLayerStack();
      onLayerStackChange?.(updatedStack);
    },
    [manager, onLayerStackChange],
  );

  const handleSimulate = useCallback(async () => {
    if (!manager) return;

    setIsSimulating(true);
    try {
      // Create substrate engine instance - would need proper ColorEngine initialization
      const substrateEngine = new SubstrateEngine({} as any); // eslint-disable-line @typescript-eslint/no-explicit-any

      const result = substrateEngine.simulateLayerStack(layerStack);
      setSimulationResult(result);
      onSimulationResult?.(result);
    } catch (error) {
      console.error("Simulation failed:", error);
    } finally {
      setIsSimulating(false);
    }
  }, [manager, layerStack, onSimulationResult]);

  const handleExport = useCallback(
    (format: "flattened" | "separated" | "stack") => {
      if (!manager) return;

      if (format === "stack") {
        const exportData = manager.export();
        const blob = new Blob([exportData], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `layer-stack-${Date.now()}.json`;
        a.click();
        URL.revokeObjectURL(url);
      } else if (simulationResult) {
        const substrateEngine = new SubstrateEngine({} as any); // eslint-disable-line @typescript-eslint/no-explicit-any
        const exportData = substrateEngine.exportLayerStack(layerStack, format);
        const blob = new Blob([JSON.stringify(exportData, null, 2)], {
          type: "application/json",
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `layer-export-${format}-${Date.now()}.json`;
        a.click();
        URL.revokeObjectURL(url);
      }
    },
    [manager, layerStack, simulationResult],
  );

  const handleImport = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        const imported = LayerStackManager.import(content);
        if (imported) {
          const updatedStack = imported.getLayerStack();
          onLayerStackChange?.(updatedStack);
        }
      };
      reader.readAsText(file);
    },
    [onLayerStackChange],
  );

  const sortedLayers = manager ? manager.getSortedLayers().reverse() : []; // Reverse to show top layer first

  return (
    <div
      className={`bg-white rounded-lg shadow-sm border border-gray-200 ${className}`}
    >
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Layer Stack</h3>
          <div className="flex items-center space-x-2">
            {/* Export/Import Buttons */}
            <button
              onClick={() => setShowExportOptions(!showExportOptions)}
              className="flex items-center space-x-1 px-3 py-1 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Download className="w-4 h-4" />
              <span>Export</span>
            </button>

            <button
              onClick={() => fileInputRef.current?.click()}
              className="flex items-center space-x-1 px-3 py-1 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Upload className="w-4 h-4" />
              <span>Import</span>
            </button>

            {/* Simulate Button */}
            <button
              onClick={handleSimulate}
              disabled={
                isSimulating || !manager || manager.getVisibleLayerCount() === 0
              }
              className="flex items-center space-x-1 px-3 py-1 text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSimulating ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Simulating...</span>
                </>
              ) : (
                <>
                  <Layers className="w-4 h-4" />
                  <span>Simulate</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Export Options Dropdown */}
        {showExportOptions && (
          <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => handleExport("stack")}
                className="px-3 py-1 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50"
              >
                Export Stack
              </button>
              <button
                onClick={() => handleExport("flattened")}
                disabled={!simulationResult}
                className="px-3 py-1 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
              >
                Export Flattened
              </button>
              <button
                onClick={() => handleExport("separated")}
                disabled={!simulationResult}
                className="px-3 py-1 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
              >
                Export Separated
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Layer List */}
      <div className="p-4">
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {sortedLayers.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Layers className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>No layers in stack</p>
              <p className="text-sm">Add a layer to get started</p>
            </div>
          ) : (
            sortedLayers.map((layer, index) => (
              <LayerEditor
                key={layer.id}
                layer={layer}
                onUpdate={(updates) => handleLayerUpdate(layer.id, updates)}
                onDelete={() => handleLayerDelete(layer.id)}
                onDuplicate={() => handleLayerDuplicate(layer.id)}
                onMoveUp={() => handleLayerMoveUp(layer.id)}
                onMoveDown={() => handleLayerMoveDown(layer.id)}
                canMoveUp={index < sortedLayers.length - 1}
                canMoveDown={index > 0}
              />
            ))
          )}
        </div>

        {/* Add Layer Button */}
        <button
          onClick={handleAddLayer}
          className="mt-4 w-full flex items-center justify-center space-x-2 px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-gray-400 hover:text-gray-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          <span>Add Layer</span>
        </button>
      </div>

      {/* Hidden file input for import */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".json"
        onChange={handleImport}
        className="hidden"
      />

      {/* Simulation Result Summary */}
      {simulationResult && (
        <div className="p-4 border-t border-gray-200 bg-gray-50">
          <h4 className="font-medium text-gray-900 mb-2">Simulation Result</h4>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-600">Final Color:</span>
              <div className="flex items-center space-x-2 mt-1">
                <div
                  className="w-6 h-6 rounded border border-gray-300"
                  style={{
                    backgroundColor: simulationResult.flattenedResult.hex,
                  }}
                />
                <span className="font-mono">
                  {simulationResult.flattenedResult.hex}
                </span>
              </div>
            </div>
            <div>
              <span className="text-gray-600">Total Ink Usage:</span>
              <div className="font-medium">
                {simulationResult.compositeMetrics.totalInkUsage.toFixed(2)} ml
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
